<template>
  <div class="flex flex-col gap-1 sm:gap-2 w-full">
    <label v-if="label" :class="labelClass">
      {{ label }}
      <span v-if="required" class="text-red-600">*</span>
    </label>

    <div class="relative" ref="triggerRef">
      <!-- Dropdown trigger button -->
      <button
        @click="toggleDropdown"
        @keydown.down.prevent="openAndFocus(0)"
        @keydown.enter.prevent="toggleDropdown"
        @keydown.space.prevent="toggleDropdown"
        type="button"
        class="flex items-center justify-between bg-white border border-gray-300 rounded-[6px] px-[13px] py-[9px] w-full h-[38px] shadow-sm hover:border-[#D0A444] transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#D0A444]/20 focus:border-[#D0A444] text-sm md:text-md leading-[20px] min-w-full"
        :class="[
          {
            'border-[#D0A444] ring-2 ring-[#D0A444]/20': isDropdownOpen,
            'border-red-300 focus:border-red-500 focus:ring-red-200':
              hasError && !isDropdownOpen,
          },
          triggerClass,
        ]"
        :disabled="disabled"
        aria-haspopup="listbox"
        :aria-expanded="isDropdownOpen"
      >
        <span
          class="text-base leading-[20px] font-normal truncate text-left"
          :class="
            !modelValue && placeholder ? 'text-gray-500' : 'text-gray-700'
          "
        >
          {{ displayValue || placeholder || "Sélectionner..." }}
        </span>
        <!-- Dropdown arrow with rotation animation -->
        <svg
          viewBox="0 0 10 6"
          fill="currentColor"
          class="w-2.5 h-2.5 flex-shrink-0 transition-transform duration-200 text-primary"
          :class="{ 'rotate-180': isDropdownOpen }"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0.292893 0.292893C0.683416 -0.097631 1.31658 -0.097631 1.7071 0.292893L4.99999 3.58579L8.29288 0.292893C8.6834 -0.0976311 9.31657 -0.0976311 9.70709 0.292893C10.0976 0.683417 10.0976 1.31658 9.70709 1.70711L5.7071 5.70711C5.31657 6.09763 4.68341 6.09763 4.29289 5.70711L0.292893 1.70711C-0.0976309 1.31658 -0.0976309 0.683417 0.292893 0.292893Z"
            fill="currentColor"
          />
        </svg>
      </button>

      <!-- Dropdown menu -->
      <ClientOnly>
        <teleport to="body" :disabled="!props.useTeleport">
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <div
              v-if="isDropdownOpen"
              ref="menuRef"
              class="bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto outline-none"
              :class="[
                dropdownClass,
                props.useTeleport ? 'absolute' : 'absolute',
                !props.useTeleport ? 'mt-1' : '',
                props.highZIndex ? 'z-[10010]' : 'z-40',
              ]"
              :style="
                props.useTeleport
                  ? floatingStyle
                  : { position: 'absolute', left: '0', right: '0', top: '100%' }
              "
              tabindex="-1"
              role="listbox"
              @keydown.down.prevent="moveActive(1)"
              @keydown.up.prevent="moveActive(-1)"
              @keydown.esc.prevent="closeDropdown()"
              @keydown.enter.prevent="confirmActive()"
            >
              <!-- Search input -->
              <div
                v-if="props.searchable"
                class="sticky top-0 bg-white p-1.5 sm:p-2 border-b border-gray-100"
              >
                <input
                  ref="searchInputRef"
                  v-model="searchQuery"
                  type="text"
                  :placeholder="searchPlaceholder || 'Rechercher...'"
                  class="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-base border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#D0A444]/20 focus:border-[#D0A444]"
                  @keydown.down.prevent="moveActive(1)"
                  @keydown.up.prevent="moveActive(-1)"
                  @keydown.enter.prevent="confirmActive()"
                  @keydown.esc.prevent="closeDropdown()"
                />
              </div>

              <div class="py-0.5 sm:py-1">
                <template v-if="filteredOptions.length > 0">
                  <button
                    v-for="(option, idx) in filteredOptions"
                    :key="getOptionValue(option)"
                    :ref="
                      (el: Element | ComponentPublicInstance | null) =>
                        (optionRefs[idx] = el as HTMLElement)
                    "
                    @pointerdown.prevent="selectOption(option)"
                    @mousedown.prevent="selectOption(option)"
                    @click.prevent="selectOption(option)"
                    type="button"
                    class="w-full text-left px-2 sm:px-3 py-1.5 sm:py-2 text-base font-normal hover:bg-gray-50 transition-colors duration-150 focus:outline-none focus:bg-[#D0A444]/10"
                    :class="{
                      'text-[#D0A444] bg-[#D0A444]/5':
                        modelValue === getOptionValue(option) ||
                        idx === activeIndex,
                      'text-gray-700':
                        modelValue !== getOptionValue(option) &&
                        idx !== activeIndex,
                    }"
                    role="option"
                    :aria-selected="modelValue === getOptionValue(option)"
                  >
                    {{ getOptionLabel(option) }}
                  </button>
                </template>
                <div
                  v-else
                  class="px-2 sm:px-3 py-1.5 sm:py-2 text-base text-gray-500"
                >
                  Aucun résultat
                </div>
              </div>
            </div>
          </Transition>
        </teleport>
      </ClientOnly>
    </div>

    <!-- Error message -->
    <Transition
      enter-active-class="transition duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <span v-if="errorMessage" class="text-sm text-red-600">
        {{ errorMessage }}
      </span>
    </Transition>
  </div>
</template>

<script setup lang="ts">
// Keep dependency-free positioning for reliability
import { onBeforeUnmount, nextTick, ref, computed, watch } from "vue";
import { useRoute } from "vue-router";

interface Option {
  label: string;
  value: string | number;
}

const props = withDefaults(
  defineProps<{
    modelValue?: string | number | null;
    options: Option[] | string[];
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    errorMessage?: string;
    labelClass?: string;
    triggerClass?: string;
    dropdownClass?: string;
    useTeleport?: boolean;
    searchable?: boolean;
    searchPlaceholder?: string;
    highZIndex?: boolean;
  }>(),
  {
    modelValue: null,
    placeholder: "",
    required: false,
    disabled: false,
    labelClass: "text-base font-medium text-gray-700 leading-6",
    triggerClass: "",
    dropdownClass: "",
    useTeleport: true,
    searchable: false,
    searchPlaceholder: "",
    highZIndex: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string | number | null): void;
  (e: "change", value: string | number | null): void;
  (e: "blur"): void;
}>();

// Local state
const isDropdownOpen = ref(false);
const triggerRef = ref<HTMLElement | null>(null);
const menuRef = ref<HTMLElement | null>(null);
const optionRefs = ref<HTMLElement[]>([]);
const activeIndex = ref<number>(-1);
const floatingStyle = ref<{
  position: "fixed";
  left: string;
  top: string;
  width: string;
}>({
  position: "fixed",
  left: "0px",
  top: "0px",
  width: "0px",
});
const searchQuery = ref("");
const searchInputRef = ref<HTMLInputElement | null>(null);

const route = useRoute();

// Helper: accent-insensitive, case-insensitive normalization for search
const normalizeText = (s: string) =>
  (s ?? "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

// Computed
const hasError = computed(() => !!props.errorMessage);

const normalizedOptions = computed<(Option | string)[]>(
  () => props.options as (Option | string)[],
);

const filteredOptions = computed<(Option | string)[]>(() => {
  const qRaw = searchQuery.value.trim();
  const q = normalizeText(qRaw);
  if (!props.searchable || q === "") return normalizedOptions.value;
  return normalizedOptions.value.filter((opt) =>
    normalizeText(getOptionLabel(opt)).includes(q),
  );
});

const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const option = normalizedOptions.value.find(
    (opt) => getOptionValue(opt) === props.modelValue,
  );
  return option ? getOptionLabel(option) : String(props.modelValue ?? "");
});

// Methods
const getOptionValue = (option: Option | string): string | number => {
  return typeof option === "string" ? option : option.value;
};

const getOptionLabel = (option: Option | string): string => {
  return typeof option === "string" ? option : option.label;
};

const positionFloating = () => {
  if (!triggerRef.value || !menuRef.value) return;
  const rect = triggerRef.value.getBoundingClientRect();
  const menuEl = menuRef.value;
  // Default below the trigger with small offset
  let left = rect.left;
  let top = rect.bottom + 4;
  // Constrain within viewport and flip if needed
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const menuWidth = menuEl.offsetWidth || 280;
  const menuHeight = menuEl.offsetHeight || 240;
  if (left + menuWidth > vw - 8) left = Math.max(8, vw - menuWidth - 8);
  if (top + menuHeight > vh - 8) top = rect.top - 4 - menuHeight;
  if (top < 8) top = 8;

  // Check if dropdown has min-width class, if so, don't constrain width to trigger
  const hasMinWidth = props.dropdownClass?.includes("min-w-");

  floatingStyle.value = {
    position: "fixed",
    left: `${left}px`,
    top: `${top}px`,
    width: hasMinWidth ? "auto" : `${rect.width}px`,
  };
};

const handleGlobalClick = (e: Event) => {
  const t = e.target as Node;
  if (triggerRef.value?.contains(t) || menuRef.value?.contains(t)) return;
  closeDropdown();
};

const handleGlobalKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape") closeDropdown();
};

const openAndFocus = (index = 0) => {
  openDropdown();
  activeIndex.value = index;
  // If searchable, focus search input; else focus active option
  nextTick(() => {
    if (props.searchable) {
      searchInputRef.value?.focus();
    } else {
      focusActive();
    }
  });
};

const focusActive = () => {
  nextTick(() => {
    const el = optionRefs.value[activeIndex.value];
    el?.focus();
  });
};

const moveActive = (dir: 1 | -1) => {
  const total = (filteredOptions.value as any[]).length;
  if (total === 0) return;
  activeIndex.value = (activeIndex.value + dir + total) % total;
  focusActive();
};

const confirmActive = () => {
  const option = (filteredOptions.value as any[])[activeIndex.value];
  if (option) selectOption(option);
};

const openDropdown = () => {
  if (isDropdownOpen.value || props.disabled) return;
  isDropdownOpen.value = true;
  searchQuery.value = ""; // reset search each time
  nextTick(() => {
    if (props.useTeleport) {
      positionFloating();
    }
    // Pick selected index if exists
    const idx = (filteredOptions.value as (Option | string)[]).findIndex(
      (o) => getOptionValue(o) === props.modelValue,
    );
    activeIndex.value = idx >= 0 ? idx : 0;
    if (props.searchable) {
      searchInputRef.value?.focus();
    } else {
      focusActive();
    }
    document.addEventListener("click", handleGlobalClick, true);
    document.addEventListener("keydown", handleGlobalKeydown, true);
    if (props.useTeleport) {
      window.addEventListener("scroll", positionFloating, true);
      window.addEventListener("resize", positionFloating, {
        passive: true,
      } as any);
    }
  });
};

const closeDropdown = () => {
  if (!isDropdownOpen.value) return;
  isDropdownOpen.value = false;
  document.removeEventListener("click", handleGlobalClick, true);
  document.removeEventListener("keydown", handleGlobalKeydown, true);
  if (props.useTeleport) {
    window.removeEventListener("scroll", positionFloating, true);
    window.removeEventListener("resize", positionFloating as any);
  }
};

const toggleDropdown = () => {
  isDropdownOpen.value ? closeDropdown() : openDropdown();
};

const selectOption = (option: Option | string) => {
  const value = getOptionValue(option);
  emit("update:modelValue", value);
  emit("change", value);
  closeDropdown();
  searchQuery.value = "";
  nextTick(() => {
    triggerRef.value?.focus();
    emit("blur");
  });
};

watch(
  () => route.fullPath,
  () => closeDropdown(),
);

watch(searchQuery, () => {
  // Reset active to first filtered option when searching
  activeIndex.value = 0;
});

onBeforeUnmount(() => {
  closeDropdown();
});

// Expose
defineExpose({
  closeDropdown,
  toggleDropdown,
});
</script>

<style scoped>
/* Additional custom styles if needed */
</style>
